import { readerApi } from "./api";
import {
  cacheArticle,
  cacheArtwork,
  cachedOfflineBodyVersions,
  clearOfflineReadingData,
  replaceOfflineCatalog,
  saveOfflineSyncState,
} from "./storage";
import type { OfflineSyncState, ReaderItem } from "./types";

const OFFLINE_SYNC_LOCK = "imprint-offline-replica-v1";
const MAX_MANIFEST_PAGES = 100;
const BUNDLE_SIZE = 8;
const QUOTA_USAGE_LIMIT = 0.8;

export type OfflineSyncObserver = (state: OfflineSyncState) => void;

let localReplicaTail: Promise<unknown> = Promise.resolve();

function withReplicaLock<T>(operation: () => Promise<T>): Promise<T> {
  const queued = localReplicaTail.catch(() => undefined).then(() => navigator.locks
    ? navigator.locks.request(OFFLINE_SYNC_LOCK, { mode: "exclusive" }, operation)
    : operation());
  localReplicaTail = queued;
  return queued;
}

export async function clearOfflineReplica(userId: string): Promise<void> {
  return withReplicaLock(() => clearOfflineReadingData(userId));
}

export async function syncOfflineReplica(userId: string, observe: OfflineSyncObserver = () => undefined, isCurrent = () => true): Promise<OfflineSyncState> {
  return withReplicaLock(() => performOfflineSync(userId, observe, isCurrent));
}

class SyncCancelled extends Error {}

async function performOfflineSync(userId: string, observe: OfflineSyncObserver, isCurrent: () => boolean): Promise<OfflineSyncState> {
  const check = () => { if (!isCurrent()) throw new SyncCancelled(); };
  const starting = syncState("syncing", "Checking your offline library…");
  try {
    check();
    await publish(userId, starting, observe);
    const [manifest, placements, storage] = await Promise.all([loadManifest(check), loadLibraryPlacements(check), prepareStorage()]);
    check();
    for (const item of manifest) {
      const placement = placements.get(item.id);
      if (placement) item.library_placement = placement;
    }
    await replaceOfflineCatalog(userId, manifest);
    const existing = await cachedOfflineBodyVersions(userId);
    const active = manifest.filter((item) => isReadableActiveItem(item));
    const missing = active
      .filter((item) => existing.get(item.id) !== item.content_version)
      .sort((left, right) => offlinePriority(left) - offlinePriority(right)
        || String(right.updated_at || right.created_at || "").localeCompare(String(left.updated_at || left.created_at || "")));
    const projectedBytes = missing.reduce((sum, item) => sum + Number(item.markdown_byte_count || 0), 0);
    const availableBytes = storage.quota ? Math.max(0, Math.floor(storage.quota * QUOTA_USAGE_LIMIT) - storage.usage) : Number.POSITIVE_INFINITY;
    let selectedBytes = 0;
    const selectedMissing = missing.filter((item) => {
      const bytes = Number(item.markdown_byte_count || 0);
      if (selectedBytes + bytes > availableBytes) return false;
      selectedBytes += bytes;
      return true;
    });
    const quotaLimited = selectedMissing.length < missing.length && projectedBytes > availableBytes;

    let downloaded = 0;
    let downloadedBytes = 0;
    const failures: string[] = [];
    for (let offset = 0; offset < selectedMissing.length; offset += BUNDLE_SIZE) {
      check();
      if (document.visibilityState === "hidden") {
        failures.push("Sync paused while Imprint is in the background.");
        break;
      }
      const chunk = selectedMissing.slice(offset, offset + BUNDLE_SIZE);
      const bundle = await readerApi.offlineBundle(chunk.map((item) => ({ id: item.id, content_version: item.content_version as string })));
      check();
      for (const item of bundle.items) {
        await cacheArticle(userId, item);
        downloaded += 1;
        downloadedBytes += Number(item.markdown_byte_count || 0);
      }
      for (const unavailable of bundle.unavailable) {
        if (unavailable.code === "bundle_byte_limit") {
          const requested = chunk.find((item) => item.id === unavailable.id);
          if (!requested) continue;
          const detail = await readerApi.item(requested.id);
          check();
          const markdownBytes = new TextEncoder().encode(detail.markdown || "").byteLength;
          if (detail.extraction_id === requested.content_version && markdownBytes > 0) {
            await cacheArticle(userId, { ...detail, content_version: requested.content_version, markdown_byte_count: markdownBytes });
            downloaded += 1;
            downloadedBytes += markdownBytes;
            continue;
          }
        }
        if (unavailable.code !== "markdown_unavailable") failures.push(`${unavailable.id}: ${unavailable.code}`);
      }
      await publish(userId, syncState("syncing", `Saving articles ${Math.min(offset + chunk.length, selectedMissing.length)} of ${selectedMissing.length}…`, {
        item_count: manifest.length,
        body_count: active.length - missing.length + downloaded,
        total_body_bytes: downloadedBytes,
        usage_bytes: storage.usage,
        quota_bytes: storage.quota,
        persistent: storage.persistent,
      }), observe);
    }

    check();
    await syncActiveArtwork(userId, active, isCurrent);
    check();
    const persistedVersions = await cachedOfflineBodyVersions(userId);
    const bodyCount = active.filter((item) => persistedVersions.get(item.id) === item.content_version).length;
    if (quotaLimited) failures.push("Not enough browser storage to download every active article.");
    const complete = bodyCount === active.length && failures.length === 0;
    const finished = syncState(complete ? "ready" : "partial", complete
      ? `${bodyCount} articles ready offline.`
      : failures[0] || `${bodyCount} of ${active.length} articles ready offline.`, {
      item_count: manifest.length,
      body_count: bodyCount,
      total_body_bytes: active.reduce((sum, item) => sum + Number(item.markdown_byte_count || 0), 0),
      completed_at: new Date().toISOString(),
      usage_bytes: storage.usage + downloadedBytes,
      quota_bytes: storage.quota,
      persistent: storage.persistent,
    });
    await publish(userId, finished, observe);
    return finished;
  } catch (error) {
    if (error instanceof SyncCancelled || !isCurrent()) return syncState("partial", "Offline sync paused.");
    const failed = syncState("error", error instanceof Error ? friendlySyncError(error) : "Offline sync failed.");
    await publish(userId, failed, observe);
    throw error;
  }
}

async function syncActiveArtwork(userId: string, items: ReaderItem[], isCurrent: () => boolean): Promise<void> {
  const candidates = items.filter((item) => item.artwork_state === "ready" && item.artwork?.color_path);
  await forEachBounded(candidates, 3, async (item) => {
    const path = item.artwork?.color_path;
    if (!path || !isCurrent()) return;
    try {
      const response = await fetch(path, { credentials: "same-origin" });
      if (!isCurrent()) return;
      const declaredBytes = Number(response.headers.get("content-length") || 0);
      if (!response.ok || (declaredBytes && declaredBytes > 64 * 1024 * 1024)) return;
      await cacheArtwork(userId, path, await response.blob(), item.id);
    } catch {
      // Artwork is optional; text completeness remains the offline readiness gate.
    }
  });
}

async function forEachBounded<T>(values: T[], concurrency: number, operation: (value: T) => Promise<void>): Promise<void> {
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, values.length) }, async () => {
    while (cursor < values.length) {
      const index = cursor;
      cursor += 1;
      await operation(values[index]);
    }
  }));
}

async function loadManifest(check: () => void): Promise<ReaderItem[]> {
  const items: ReaderItem[] = [];
  const ids = new Set<string>();
  let cursor = "";
  for (let page = 0; page < MAX_MANIFEST_PAGES; page += 1) {
    check();
    const envelope = await readerApi.offlineManifest(cursor);
    check();
    for (const item of envelope.items) {
      if (ids.has(item.id)) throw new Error("offline_manifest_duplicate");
      ids.add(item.id);
      items.push(item);
    }
    if (!envelope.has_more) return items;
    if (!envelope.next_cursor || envelope.next_cursor === cursor) throw new Error("offline_manifest_cursor_invalid");
    cursor = envelope.next_cursor;
  }
  throw new Error("offline_manifest_too_large");
}

async function loadLibraryPlacements(check: () => void): Promise<Map<string, NonNullable<ReaderItem["library_placement"]>>> {
  const placements = new Map<string, NonNullable<ReaderItem["library_placement"]>>();
  let cursor = "";
  for (let page = 0; page < MAX_MANIFEST_PAGES; page += 1) {
    check();
    const envelope = await readerApi.listItems("active", "library", cursor);
    check();
    for (const item of envelope.items) if (item.library_placement) placements.set(item.id, item.library_placement);
    if (!envelope.has_more) return placements;
    if (!envelope.next_cursor || envelope.next_cursor === cursor) throw new Error("offline_library_cursor_invalid");
    cursor = envelope.next_cursor;
  }
  throw new Error("offline_library_too_large");
}

async function prepareStorage(): Promise<{ usage: number; quota: number; persistent?: boolean }> {
  let persistent: boolean | undefined;
  try {
    if (navigator.storage?.persisted) persistent = await navigator.storage.persisted();
    if (!persistent && navigator.storage?.persist) persistent = await navigator.storage.persist();
  } catch {
    persistent = false;
  }
  try {
    const estimate = await navigator.storage?.estimate?.();
    return {
      usage: Number.isSafeInteger(estimate?.usage) ? estimate?.usage as number : 0,
      quota: Number.isSafeInteger(estimate?.quota) ? estimate?.quota as number : 0,
      persistent,
    };
  } catch {
    return { usage: 0, quota: 0, persistent };
  }
}

function isReadableActiveItem(item: ReaderItem): boolean {
  return (item.status === "inbox" || item.status === "queue")
    && item.reader_status !== "unavailable"
    && Boolean(item.content_version)
    && Number(item.markdown_byte_count || 0) > 0;
}

function offlinePriority(item: ReaderItem): number {
  if (item.library_placement?.kind === "continue") return 0;
  if (item.library_placement?.kind === "lift") return 1;
  return item.status === "inbox" ? 2 : 3;
}

function syncState(
  status: OfflineSyncState["status"],
  message: string,
  overrides: Partial<OfflineSyncState> = {},
): OfflineSyncState {
  return {
    status,
    item_count: 0,
    body_count: 0,
    total_body_bytes: 0,
    completed_at: "",
    message,
    ...overrides,
  };
}

async function publish(userId: string, state: OfflineSyncState, observe: OfflineSyncObserver): Promise<void> {
  await saveOfflineSyncState(userId, state);
  observe(state);
  try {
    const channel = new BroadcastChannel("imprint-offline-sync");
    channel.postMessage(state);
    channel.close();
  } catch { /* BroadcastChannel is optional. */ }
}

function friendlySyncError(error: Error): string {
  if (error.name === "QuotaExceededError") return "Browser storage filled before offline sync could finish.";
  if (error.message === "offline_manifest_too_large") return "Your library is larger than this version of offline sync supports.";
  return "Could not finish offline sync. Your last complete offline library is still available.";
}
